<template>
  <Onboarding>
    <div class="mx-auto flex w-full max-w-4xl flex-col items-start justify-center py-2">
      <div class="mb-8 w-full text-center">
        <h1 class="mb-2 text-3xl font-bold text-white">Create New File</h1>
        <p class="text-gray-400">Start with a template or create from scratch</p>
      </div>
      <div class="mb-8 w-full rounded-lg bg-gray-800 p-6">
        <h2 class="mb-4 text-xl font-semibold text-white">Choose a Template</h2>

        <DTabs value="my-templates" class="w-full">
          <DTabList class="mb-6 flex border-b border-gray-700">
            <DTab value="my-templates" class="px-4 py-2 text-gray-300 transition-colors hover:text-white">
              My Templates
            </DTab>
            <DTab value="public-templates" class="px-4 py-2 text-gray-300 transition-colors hover:text-white">
              Public Templates
            </DTab>
          </DTabList>

          <DTabPanels>
            <DTabPanel value="my-templates">
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <!-- My Templates Grid -->
                <div
                  v-for="template in myTemplates"
                  :key="template.id"
                  @click="selectTemplate(template)"
                  :class="[
                    'cursor-pointer rounded-lg border-2 p-4 transition-all',
                    selectedTemplate?.id === template.id
                      ? 'border-blue-500 bg-blue-600'
                      : template.isBlank
                        ? 'border-dashed border-gray-600 bg-gray-700 hover:border-gray-500'
                        : 'border-gray-600 bg-gray-700 hover:border-gray-500 hover:bg-gray-600',
                  ]">
                  <div class="text-center text-gray-300">
                    <i :class="[template.icon, 'mb-2 text-2xl']"></i>
                    <p class="font-medium">{{ template.name }}</p>
                    <p class="text-xs text-gray-500">
                      {{ template.description }}
                    </p>
                  </div>
                </div>
              </div>
            </DTabPanel>

            <DTabPanel value="public-templates">
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <!-- Public Templates Grid -->
                <div
                  v-for="template in publicTemplates"
                  :key="template.id"
                  @click="selectTemplate(template)"
                  :class="[
                    'cursor-pointer rounded-lg border-2 bg-gray-700 p-4 transition-all',
                    selectedTemplate?.id === template.id
                      ? 'border-blue-500 bg-blue-600'
                      : 'border-gray-600 hover:border-gray-500 hover:bg-gray-600',
                  ]">
                  <div class="text-center text-gray-300">
                    <i :class="[template.icon, 'mb-2 text-2xl']"></i>
                    <p class="font-medium">{{ template.name }}</p>
                    <p class="text-xs text-gray-500">
                      {{ template.description }}
                    </p>
                  </div>
                </div>
              </div>
            </DTabPanel>
          </DTabPanels>
        </DTabs>
      </div>
      <div class="w-full rounded-lg bg-gray-800 p-6">
        <h2 class="mb-4 text-xl font-semibold text-white">File Details</h2>

        <!-- Selected Template Display -->
        <div v-if="selectedTemplate" class="mb-4 rounded-lg bg-gray-700 p-3">
          <div class="flex items-center text-sm text-gray-300">
            <i :class="[selectedTemplate.icon, 'mr-2']"></i>
            <span
              >Selected Template: <strong>{{ selectedTemplate.name }}</strong></span
            >
            <DButton
              @click="
                () => {
                  selectedTemplate = null;
                  newFileName = 'New File';
                }
              "
              text
              severity="secondary"
              size="small"
              class="ml-auto">
              <i class="pi pi-times"></i>
            </DButton>
          </div>
        </div>

        <div class="space-y-4">
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-300">Workspace</label>

            <DSelect
              :options="workspaceStore.workspaces"
              optionValue="code"
              optionLabel="name"
              v-model="currentWorkspace"
              placeholder="Select a workspace"
              class="w-full text-gray-300" />
            <span class="text-red-500" v-if="errors.currentWorkspace">{{ errors.currentWorkspace }}</span>
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-300">Space</label>
            <DSelect
              :options="spaceStore.spaces"
              optionValue="code"
              optionLabel="name"
              v-model="currentSpace"
              placeholder="Select a Space"
              class="w-full text-gray-300" />
            <span class="text-red-500" v-if="errors.currentSpace">{{ errors.currentSpace }}</span>
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-300">Folder</label>
            <DSelect
              :options="folderStore.folders"
              optionValue="code"
              optionLabel="name"
              v-model="currentFolder"
              placeholder="Select a Folder"
              class="w-full text-gray-300" />
            <span class="text-red-500" v-if="errors.currentFolder">{{ errors.currentFolder }}</span>
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-300">File Name</label>
            <DInputText v-model="newFileName" placeholder="Enter file name" class="w-full text-gray-300" />
            <span class="text-red-500" v-if="errors.newFileName">{{ errors.newFileName }}</span>
          </div>

          <div class="flex items-center justify-end gap-3 pt-4">
            <DButton label="Cancel" @click="router.push('/')" class="bg-surface-900 border-none px-6 py-2" />

            <DButton
              label="Create File"
              @click.stop.prevent="create"
              class="px-6 py-2"
              severity="primary"
              :disabled="disableCreate" />
          </div>
        </div>
      </div>
    </div>
  </Onboarding>
</template>

<script setup>
import Onboarding from '@/components/layout/Onboarding.vue';

import { useAppAction } from '@/composables/useAppAction';
import { useCruxStore } from '@/stores/api/crux';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useFolderStore } from '@/stores/api/folder';
import { useSpaceStore } from '@/stores/api/space';
import { useWorkspaceStore } from '@/stores/api/workspace';
import {
  DButton,
  DInputText,
  DSelect,
  DTab,
  DTabList,
  DTabPanel,
  DTabPanels,
  DTabs,
  useToast,
} from '@disguise/cloud-ui';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const cruxStore = useCruxStore();
const cruxModuleStore = useCruxModuleStore();
const folderStore = useFolderStore();
const spaceStore = useSpaceStore();
const workspaceStore = useWorkspaceStore();
const toast = useToast();

const currentWorkspace = ref(null);
const newFileName = ref('New File');
const selectedTemplate = ref(null);
const currentSpace = ref(null);
const currentFolder = ref(null);
const errors = ref({});
const { handleApiResponse } = useAppAction();

// Template data
const myTemplates = ref([
  {
    id: 'blank',
    name: 'Blank Scene',
    description: 'Start with an empty scene',
    icon: 'pi pi-plus',
    isBlank: true,
  },
]);

const publicTemplates = ref([
  {
    id: 'led-setup',
    name: 'Basic LED Setup',
    description: 'Standard LED configuration',
    icon: 'pi pi-desktop',
  },
  {
    id: 'workspaceor-array',
    name: 'Workspace or Array',
    description: 'Multi-workspace or setup',
    icon: 'pi pi-video',
  },
  {
    id: 'stage-design',
    name: 'Stage Design',
    description: 'Concert stage template',
    icon: 'pi pi-box',
  },
]);

const selectTemplate = (template) => {
  selectedTemplate.value = template;
  if (template.name !== 'Blank Scene') {
    newFileName.value = `New ${template.name}`;
  }
};

const loadSpaces = async () => {
  if (currentWorkspace.value) {
    await spaceStore.fetchForWorkspace(currentWorkspace.value);
  }
};

onMounted(() => {
  currentWorkspace.value = workspaceStore.currentWorkspace;
  // Auto-select blank template by default
  selectedTemplate.value = myTemplates.value.find((t) => t.id === 'blank');
});
const disableCreate = ref(false);
let _errorTimer = null;

onBeforeUnmount(() => {
  if (_errorTimer) clearTimeout(_errorTimer);
});

const create = async () => {
  errors.value = {
    currentWorkspace: !currentWorkspace.value ? 'Workspace is required' : null,
    currentSpace: !currentSpace.value ? 'Please select a space' : null,
    currentFolder: !currentFolder.value ? 'Please select a folder' : null,
    newFileName: !newFileName.value.trim() ? 'File name is required' : null,
  };
  if (Object.values(errors.value).some((error) => error)) {
    disableCreate.value = true;
    toast.add({
      summary: 'Please fill in all required fields',
      severity: 'error',
      group: 'br',
      life: 3000,
    });
    _errorTimer = setTimeout(() => {
      errors.value = {};
      disableCreate.value = false;
    }, 3000);
    return;
  }

  if (currentFolder.value) {
    const response = await cruxStore.store({
      name: newFileName.value || 'New File',
      folder: currentFolder.value,
    });

    const { error } = response;
    if (error) {
      handleApiResponse(error);
      return;
    }
    if (response && response.asset) {
      await cruxModuleStore.createDefaultModulesForAsset(response.code);
      // Navigate to the editor with the created asset
      router.push(`/editor/${response.asset}`);
    }
  }
};

watch(
  () => workspaceStore.currentWorkspace,
  (_) => {
    currentWorkspace.value = workspaceStore.currentWorkspace;
  },
  { deep: true }
);
watch(currentWorkspace, async (newVal) => {
  if (newVal) {
    await loadSpaces();
  }
});

watch(currentSpace, async (newVal) => {
  if (newVal) {
    await folderStore.fetchForSpace(newVal);
  }
});
</script>
